import React, { useCallback, useEffect } from 'react';
import { styled } from '@compiled/react';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import Button from '@atlaskit/button';
import { LinkIconButton } from '@atlaskit/button/new';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import RetryIcon from '@atlaskit/icon/core/retry';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Flex, Inline, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { FixedWidthPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import View from '@atlassian/jira-jql-builder-basic-textarea/src/ui/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { isCMDBEnabledInEnvironment } from '@atlassian/jira-servicedesk-cmdb-fedramp/src/index.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import { isLicensedPremiumUser } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/services/resources/get-data/index.tsx';
import { useIqlValidation } from '@atlassian/jira-servicedesk-insight-iql-validation/src/controllers/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../../../common/constants.tsx';
import PickerTrigger from '../../../../../common/ui/picker-trigger/main.tsx';
import type { TextAreaCommonProps } from '../../../../../common/ui/types.tsx';
import {
	useShouldRenderToParent,
	useVerticalMode,
} from '../../../../../controllers/feature-context.tsx';
import { PickerTriggerLayout } from '../../../../../common/ui/picker-trigger-layout/index.tsx';
import messages from './messages.tsx';

const formatLabel = (label: string, value: string | null, verticalMode = false) => {
	if (value === null || value === undefined || !value.length) {
		return verticalMode ? <PickerTriggerLayout label={label || ''}> </PickerTriggerLayout> : label;
	}

	if (verticalMode) {
		return (
			<PickerTriggerLayout label={label || ''}>
				<ValueWrapper>{value}</ValueWrapper>
			</PickerTriggerLayout>
		);
	}

	return (
		<Flex alignItems="center">
			<Text color="inherit" as="strong">
				{label}
			</Text>
			:&nbsp;<ValueWrapper>{value}</ValueWrapper>
		</Flex>
	);
};

const shouldValidateAQL = (tenantContext: TenantContext) =>
	isLicensedPremiumUser(tenantContext) && isCMDBEnabledInEnvironment();

export const AqlPickerPopup = ({
	fieldId,
	fieldDisplayName,
	isOpen,
	onOpen,
	onClose,
	onClear,
	onChange,
	onSendOptionAnalytics,
	value,
	fieldDescription,
	isIrremovable,
}: TextAreaCommonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const shouldRenderToParent = useShouldRenderToParent();
	const { workspaceId, fetchWorkspaceId } = useWorkspaceContext();
	const tenantContext = useTenantContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const verticalMode = useVerticalMode();
	const [{ unvalidatedIql, validationResult }, { onChange: onIqlChange }] = useIqlValidation({
		workspaceId,
		initialIql: value ?? '',
	});

	let aqlValidationError: string | undefined;

	if (validationResult.type === 'invalid') {
		aqlValidationError = validationResult.error;
	}

	useEffect(() => {
		if (!workspaceId) {
			fetchWorkspaceId();
		}
	}, [fetchWorkspaceId, workspaceId]);

	const handleOnValueChange = useCallback(
		(inputValue = '') => {
			// Only check AQL if it is a premium user
			if (shouldValidateAQL(tenantContext)) {
				onIqlChange(inputValue);
			}
		},
		[onIqlChange, tenantContext],
	);

	const handleOnUpdate = useCallback(
		(inputValue: string) => {
			onChange(inputValue);
			onClose();
			onSendOptionAnalytics &&
				onSendOptionAnalytics({
					isAqlValidationEnabled: shouldValidateAQL(tenantContext),
					searchStringLength: inputValue.length,
				});
		},
		[onChange, onClose, onSendOptionAnalytics, tenantContext],
	);

	const handleOnHelpClick = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
		});
		const attributes: Attributes = {
			actionType: 'close',
		};
		fireUIAnalytics(analyticsEvent, 'aqlHelpLink', attributes);
	}, [createAnalyticsEvent]);

	const handleOnTriggerClick = useCallback(() => {
		if (isOpen) {
			onClose();
		} else {
			onOpen();
			if (shouldValidateAQL(tenantContext)) {
				onIqlChange(value || '');
			}
		}
	}, [isOpen, onClose, onIqlChange, onOpen, tenantContext, value]);

	const handleOnAQLValidationRetry = useCallback(
		(inputValue: string) => () => onIqlChange(inputValue),
		[onIqlChange],
	);

	useEffect(() => {
		if (shouldValidateAQL(tenantContext) && aqlValidationError) {
			// We skip syntax errors from sentry and sent them to privacy unsafe splunk
			const isInvalidSyntaxError = aqlValidationError.includes('invalid syntax');

			fireErrorAnalytics({
				error: new Error(isInvalidSyntaxError ? 'invalid syntax' : 'unable to validate AQL query'),
				sendToPrivacyUnsafeSplunk: true,
				skipSentry: isInvalidSyntaxError,
				meta: {
					id: 'aql-validation-error',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
			});
		}
	}, [aqlValidationError, tenantContext]);

	const getAqlPickerErrorMessage = useCallback(
		(inputValue: string): React.ReactElement | string | undefined => {
			if (!shouldValidateAQL(tenantContext)) {
				return undefined;
			}

			switch (validationResult.type) {
				case 'valid':
					return unvalidatedIql !== inputValue
						? formatMessage(messages.aqlValidationInProgressMessage)
						: undefined;
				case 'invalid':
					return aqlValidationError !== 'Failed to fetch' ? (
						formatMessage(messages.inValidAqlErrorMessage)
					) : (
						<Inline alignInline="center" xcss={retryStyles}>
							<Text>{formatMessage(messages.unableToValidateAqlErrorMessage)}</Text>
							<Box xcss={retryIconStyles}>
								<Tooltip content={formatMessage(messages.retry)} position="top">
									<Button
										appearance="link"
										spacing="none"
										onClick={handleOnAQLValidationRetry(inputValue)}
										testId="jql-builder-basic.ui.jql-editor.field.text-area-picker.aql-picker-popup.retry-button"
									>
										<RetryIcon label={formatMessage(messages.retry)} />
									</Button>
								</Tooltip>
							</Box>
						</Inline>
					);
				case 'loading':
					return formatMessage(messages.aqlValidationInProgressMessage);
				default:
					return undefined;
			}
		},
		[
			tenantContext,
			validationResult.type,
			unvalidatedIql,
			formatMessage,
			aqlValidationError,
			handleOnAQLValidationRetry,
		],
	);

	const InfoIconComponent = useCallback(
		() => <QuestionCircleIcon spacing="spacious" color={token('color.icon')} label="" />,
		[],
	);

	return (
		<Popup
			key={fieldId}
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			popupComponent={FixedWidthPopupComponent}
			shouldRenderToParent={shouldRenderToParent}
			messageId="jql-builder-basic.ui.jql-editor.field.text-area-picker.aql-picker-popup.popup"
			messageType="transactional"
			trigger={(triggerProps: TriggerProps) => (
				<PickerTrigger
					{...triggerProps}
					isInvalid={false}
					isSelected={isOpen}
					hasValues={!!value}
					onClick={handleOnTriggerClick}
					onClear={onClear}
					testId={`jql-builder-basic.ui.jql-editor.aql.filter-button.${fieldId}`}
					fieldDescription={fieldDescription}
					isIrremovable={isIrremovable}
				>
					{formatLabel(
						fieldDisplayName,
						value ? formatMessage(messages.formatLabelMessage) : '',
						verticalMode,
					)}
				</PickerTrigger>
			)}
			content={({ setInitialFocusRef }) => (
				<View
					value={value ?? ''}
					getErrorMessage={getAqlPickerErrorMessage}
					onChange={handleOnUpdate}
					onValueChange={handleOnValueChange}
					placeholderText={formatMessage(messages.textAreaPlaceholder)}
					setInitialFocusRef={setInitialFocusRef}
					footerIcon={
						<Box xcss={helpStyles}>
							<Tooltip content={formatMessage(messages.aqlSyntaxHelpLinkLabel)} position="top">
								<LinkIconButton
									href="https://support.atlassian.com/jira-service-management-cloud/docs/use-assets-query-language-aql/"
									shape="circle"
									onClick={handleOnHelpClick}
									icon={InfoIconComponent}
									appearance="subtle"
									spacing="compact"
									label={formatMessage(messages.aqlSyntaxHelpLinkLabel)}
								/>
							</Tooltip>
						</Box>
					}
				/>
			)}
		/>
	);
};

export default AqlPickerPopup;

const retryStyles = xcss({
	height: token('space.200'),
});

const helpStyles = xcss({
	height: token('space.300'),
});

const retryIconStyles = xcss({ marginLeft: 'space.050' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ValueWrapper = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '200px',
});
